import { useEffect, useMemo, useRef, useState } from "react"
import {
    CalendarDays,
    ClipboardList,
    Loader2,
    RefreshCcw,
    Sparkles,
    Truck,
    Users2,
} from "lucide-react"
import toast from "react-hot-toast"
import axios from "axios"

import { adminService } from "@/services/admin.service"
import type {
    CollectionPoint,
    DeliveryCalendarDaySummary,
    DeliveryCalendarSlotSummary,
    DeliveryGroupDetail,
    DeliveryGroupSummary,
    DeliveryStaffBoardItem,
} from "@/types/admin.type"

import { AdminDeliveryAssignmentPanel } from "./adminDelivery/AssignmentPanel"
import { AdminDeliveryCalendarPanel } from "./adminDelivery/CalendarPanel"
import { AdminDeliveryTimelinePanel } from "./adminDelivery/TimelinePanel"
import { StatCard } from "./adminDelivery/Shared"
import {
    DEBUG_SCOPE,
    buildFallbackGroupDetailFromSummary,
    buildMonthStats,
    cn,
    debugLog,
    filterAndSortStaffBoard,
    filterSlotSummaries,
    isDraftGroup,
    mergeGroupDetail,
} from "./adminDelivery/adminDelivery.utils"

const extractErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const apiMessage =
            (error.response?.data as any)?.message ||
            (error.response?.data as any)?.errors?.filter(Boolean)?.join(", ")

        console.error(`${DEBUG_SCOPE} axios error`, {
            message: error.message,
            code: error.code,
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            responseData: error.response?.data,
            requestData: error.config?.data,
            params: error.config?.params,
        })

        return apiMessage || fallback
    }

    console.error(`${DEBUG_SCOPE} unknown error`, error)
    return fallback
}

const extractCollectionIdFromDeliveryArea = (value?: string) => {
    const text = String(value || "").trim()
    if (!text || !text.startsWith("COLLECTION:")) return ""
    return text.replace("COLLECTION:", "").trim()
}

const resolveCollectionPointName = (
    deliveryArea?: string,
    collectionPoints?: CollectionPoint[]
) => {
    const collectionId = extractCollectionIdFromDeliveryArea(deliveryArea)
    if (!collectionId) return undefined

    return collectionPoints?.find((item) => item.collectionId === collectionId)?.name
}

const enrichGroupSummary = (
    group: DeliveryGroupSummary,
    collectionPoints: CollectionPoint[]
): DeliveryGroupSummary => {
    const collectionId =
        group.collectionId ?? extractCollectionIdFromDeliveryArea(group.deliveryArea)

    const collectionPointName =
        group.collectionPointName ||
        resolveCollectionPointName(group.deliveryArea, collectionPoints)

    return {
        ...group,
        collectionId: collectionId || undefined,
        collectionPointName: collectionPointName || undefined,
        failedOrders: group.failedOrders ?? 0,
    }
}

const toStartOfDayUtc = (dateKey: string) => {
    const normalized = String(dateKey || "").trim()
    if (!normalized) return ""

    return `${normalized.slice(0, 10)}T00:00:00.000Z`
}

const AdminDelivery = () => {
    const today = new Date()
    const todayDateKey = adminService.toDateKey(today.toISOString())

    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [slotLoading, setSlotLoading] = useState(false)
    const [generatingGroups, setGeneratingGroups] = useState(false)
    const [autoGeneratingDrafts, setAutoGeneratingDrafts] = useState(false)
    const [assigningGroupId, setAssigningGroupId] = useState("")
    const [confirmingGroupId, setConfirmingGroupId] = useState("")
    const [error, setError] = useState("")
    const [detailNotice, setDetailNotice] = useState("")

    const autoGeneratedDateRef = useRef<Set<string>>(new Set())

    const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1)
    const [currentYear, setCurrentYear] = useState(today.getFullYear())

    const [monthSummary, setMonthSummary] = useState<DeliveryCalendarDaySummary[]>([])
    const [selectedDate, setSelectedDate] = useState("")
    const [slotSummaries, setSlotSummaries] = useState<DeliveryCalendarSlotSummary[]>([])

    const [selectedGroup, setSelectedGroup] = useState<DeliveryGroupDetail | null>(null)

    const [staffBoard, setStaffBoard] = useState<DeliveryStaffBoardItem[]>([])
    const [collectionPoints, setCollectionPoints] = useState<CollectionPoint[]>([])

    const [searchInput, setSearchInput] = useState("")
    const [keyword, setKeyword] = useState("")
    const [staffKeyword, setStaffKeyword] = useState("")

    const [assignForm, setAssignForm] = useState({
        deliveryStaffId: "",
        reason: "",
    })

    const [draggingStaff, setDraggingStaff] =
        useState<DeliveryStaffBoardItem | null>(null)
    const [selectedAssignStaff, setSelectedAssignStaff] =
        useState<DeliveryStaffBoardItem | null>(null)
    const [isAssignDropActive, setIsAssignDropActive] = useState(false)

    const filteredSlotSummaries = useMemo(
        () => filterSlotSummaries(slotSummaries, keyword),
        [slotSummaries, keyword]
    )

    const filteredStaffBoard = useMemo(
        () => filterAndSortStaffBoard(staffBoard, staffKeyword),
        [staffBoard, staffKeyword]
    )

    const monthStats = useMemo(() => buildMonthStats(monthSummary), [monthSummary])

    const syncSelectedStaffFromDetail = (
        detail: DeliveryGroupDetail,
        source?: DeliveryStaffBoardItem[]
    ) => {
        const staffSource = source ?? staffBoard
        const matchedStaff =
            staffSource.find((item) => item.deliveryStaffId === detail.deliveryStaffId) ||
            null

        setSelectedAssignStaff(matchedStaff)
        setAssignForm((prev) => ({
            ...prev,
            deliveryStaffId: detail.deliveryStaffId || "",
        }))
    }

    const patchGroupIntoLocalCollections = (detail: DeliveryGroupDetail) => {
        setSlotSummaries((current) =>
            current.map((slot) => {
                const nextGroups = slot.groups.map((group) => {
                    if (group.deliveryGroupId !== detail.deliveryGroupId) return group

                    return enrichGroupSummary(
                        {
                            ...group,
                            deliveryStaffId: detail.deliveryStaffId ?? undefined,
                            deliveryStaffName: detail.deliveryStaffName ?? undefined,
                            timeSlotId: detail.timeSlotId || group.timeSlotId,
                            timeSlotDisplay: detail.timeSlotDisplay || group.timeSlotDisplay,
                            deliveryType: detail.deliveryType || group.deliveryType,
                            deliveryArea: detail.deliveryArea || group.deliveryArea,
                            status: detail.status || group.status,
                            totalOrders: detail.totalOrders ?? group.totalOrders,
                            completedOrders:
                                detail.completedOrders ?? group.completedOrders,
                            failedOrders: detail.failedOrders ?? group.failedOrders,
                            deliveryDate: detail.deliveryDate || group.deliveryDate,
                            collectionId: detail.collectionId ?? group.collectionId,
                            collectionPointName:
                                detail.collectionPointName ?? group.collectionPointName,
                            centerLatitude:
                                detail.centerLatitude ?? group.centerLatitude,
                            centerLongitude:
                                detail.centerLongitude ?? group.centerLongitude,
                        },
                        collectionPoints
                    )
                })

                return {
                    ...slot,
                    groups: nextGroups,
                    totalGroups: nextGroups.length,
                    totalOrders: nextGroups.reduce(
                        (sum, item) => sum + (item.totalOrders ?? 0),
                        0
                    ),
                    unassignedGroups: nextGroups.filter(
                        (item) => !(item.deliveryStaffId || item.deliveryStaffName)
                    ).length,
                }
            })
        )

        setMonthSummary((current) =>
            current.map((day) => {
                const nextGroups = day.groups.map((group) => {
                    if (group.deliveryGroupId !== detail.deliveryGroupId) return group

                    return enrichGroupSummary(
                        {
                            ...group,
                            deliveryStaffId: detail.deliveryStaffId ?? undefined,
                            deliveryStaffName: detail.deliveryStaffName ?? undefined,
                            timeSlotId: detail.timeSlotId || group.timeSlotId,
                            timeSlotDisplay: detail.timeSlotDisplay || group.timeSlotDisplay,
                            deliveryType: detail.deliveryType || group.deliveryType,
                            deliveryArea: detail.deliveryArea || group.deliveryArea,
                            status: detail.status || group.status,
                            totalOrders: detail.totalOrders ?? group.totalOrders,
                            completedOrders:
                                detail.completedOrders ?? group.completedOrders,
                            failedOrders: detail.failedOrders ?? group.failedOrders,
                            deliveryDate: detail.deliveryDate || group.deliveryDate,
                            collectionId: detail.collectionId ?? group.collectionId,
                            collectionPointName:
                                detail.collectionPointName ?? group.collectionPointName,
                            centerLatitude:
                                detail.centerLatitude ?? group.centerLatitude,
                            centerLongitude:
                                detail.centerLongitude ?? group.centerLongitude,
                        },
                        collectionPoints
                    )
                })

                const normalize = (value?: string) =>
                    String(value || "")
                        .trim()
                        .toLowerCase()
                        .replace(/[\s_-]+/g, "")

                return {
                    ...day,
                    groups: nextGroups,
                    totalGroups: nextGroups.length,
                    totalOrders: nextGroups.reduce(
                        (sum, item) => sum + (item.totalOrders ?? 0),
                        0
                    ),
                    totalDraftGroups: nextGroups.filter(
                        (item) => normalize(item.status) === "draft"
                    ).length,
                    totalPendingGroups: nextGroups.filter((item) => {
                        const status = normalize(item.status)
                        return status === "pending" || status === "confirmed"
                    }).length,
                    totalAssignedGroups: nextGroups.filter(
                        (item) => normalize(item.status) === "assigned"
                    ).length,
                    totalInTransitGroups: nextGroups.filter(
                        (item) => normalize(item.status) === "intransit"
                    ).length,
                    totalCompletedGroups: nextGroups.filter(
                        (item) => normalize(item.status) === "completed"
                    ).length,
                    totalFailedGroups: nextGroups.filter(
                        (item) => normalize(item.status) === "failed"
                    ).length,
                    totalUnassignedGroups: nextGroups.filter(
                        (item) => !(item.deliveryStaffId || item.deliveryStaffName)
                    ).length,
                }
            })
        )
    }

    const keepSelectedGroupInSync = (
        groups: DeliveryGroupSummary[],
        staffSource?: DeliveryStaffBoardItem[]
    ) => {
        setSelectedGroup((current) => {
            if (!current?.deliveryGroupId) return current

            const found = groups.find(
                (item) => item.deliveryGroupId === current.deliveryGroupId
            )

            if (!found) return current

            const fallbackDetail = buildFallbackGroupDetailFromSummary(
                enrichGroupSummary(found, collectionPoints),
                current
            )

            syncSelectedStaffFromDetail(fallbackDetail, staffSource)
            return fallbackDetail
        })
    }

    const loadMonthData = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true)
            else setLoading(true)

            setError("")

            debugLog("loadMonthData:start", {
                currentMonth,
                currentYear,
                isRefresh,
            })

            const [monthResult, staffBoardResult, collectionPointResult] = await Promise.all([
                adminService.getDeliveryCalendarMonth({
                    month: currentMonth,
                    year: currentYear,
                }),
                adminService.getDeliveryStaffBoard(),
                adminService.getCollectionPoints(),
            ])

            const nextMonthDays = monthResult.days ?? []
            const nextStaffBoard = staffBoardResult ?? []
            const nextCollectionPoints = collectionPointResult ?? []

            debugLog("loadMonthData:success", {
                totalDays: nextMonthDays.length,
                staffCount: nextStaffBoard.length,
                collectionPointCount: nextCollectionPoints.length,
            })

            setMonthSummary(nextMonthDays)
            setStaffBoard(nextStaffBoard)
            setCollectionPoints(nextCollectionPoints)

            const nextSelectedDate =
                selectedDate && nextMonthDays.some((item) => item.date === selectedDate)
                    ? selectedDate
                    : currentMonth === today.getMonth() + 1 &&
                        currentYear === today.getFullYear()
                        ? todayDateKey
                        : nextMonthDays[0]?.date || ""

            setSelectedDate(nextSelectedDate)

            keepSelectedGroupInSync(
                slotSummaries.flatMap((slot) => slot.groups),
                nextStaffBoard
            )
        } catch (err) {
            console.error(`${DEBUG_SCOPE} loadMonthData error`, err)
            setError(extractErrorMessage(err, "Không thể tải lịch điều phối giao hàng."))
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    const autoGenerateDraftGroupsIfNeeded = async (date: string) => {
        if (!date) return false

        if (autoGeneratedDateRef.current.has(date)) {
            debugLog("autoGenerateDraftGroupsIfNeeded:skipped", {
                deliveryDate: date,
                reason: "already-tried",
            })
            return false
        }

        try {
            setAutoGeneratingDrafts(true)
            autoGeneratedDateRef.current.add(date)
            setError("")

            debugLog("autoGenerateDraftGroupsIfNeeded:start", {
                deliveryDate: date,
            })

            const result = await adminService.generateDraftDeliveryGroups({
                deliveryDate: date,
                maxDistanceKm: 5,
                maxOrdersPerGroup: 20,
            })

            debugLog("autoGenerateDraftGroupsIfNeeded:success", {
                deliveryDate: date,
                generatedCount: result?.length ?? 0,
                groups: result?.map((group) => ({
                    deliveryGroupId: group.deliveryGroupId,
                    groupCode: group.groupCode,
                    status: group.status,
                    totalOrders: group.totalOrders,
                    timeSlotId: group.timeSlotId,
                    timeSlotDisplay: group.timeSlotDisplay,
                    collectionId: group.collectionId,
                    collectionPointName: group.collectionPointName,
                })),
            })

            if (result?.length) {
                toast.success(`Đã tự gom ${result.length} nhóm giao`)
                return true
            }

            return false
        } catch (err) {
            console.error(`${DEBUG_SCOPE} autoGenerateDraftGroupsIfNeeded error`, err)
            setError(extractErrorMessage(err, "Không thể tự gom đơn giao hàng."))
            return false
        } finally {
            setAutoGeneratingDrafts(false)
        }
    }

    const buildSlotsByDate = async (date: string) => {
        const result = await adminService.getDeliveryCalendarSlotsByDate(date)

        return (result ?? []).map((slot) => ({
            ...slot,
            groups: (slot.groups ?? []).map((group) =>
                enrichGroupSummary(group, collectionPoints)
            ),
        }))
    }

    const loadSlotsByDate = async (date: string) => {
        if (!date) {
            setSlotSummaries([])
            return
        }

        try {
            setSlotLoading(true)
            debugLog("loadSlotsByDate:start", { date })

            let nextSlots = await buildSlotsByDate(date)

            const firstTotalGroups = nextSlots.reduce(
                (sum, item) => sum + (item.groups?.length ?? 0),
                0
            )

            debugLog("loadSlotsByDate:first-load", {
                date,
                totalSlots: nextSlots.length,
                totalGroups: firstTotalGroups,
            })

            if (firstTotalGroups === 0) {
                const generated = await autoGenerateDraftGroupsIfNeeded(date)

                if (generated) {
                    nextSlots = await buildSlotsByDate(date)
                }
            }

            debugLog("loadSlotsByDate:success", {
                date,
                totalSlots: nextSlots.length,
                totalGroups: nextSlots.reduce(
                    (sum, item) => sum + (item.groups?.length ?? 0),
                    0
                ),
            })

            debugLog("loadSlotsByDate:slots-detail", {
                date,
                slots: nextSlots.map((slot) => ({
                    timeSlotId: slot.timeSlotId,
                    timeSlotDisplay: slot.timeSlotDisplay,
                    totalGroups: slot.totalGroups,
                    totalOrders: slot.totalOrders,
                    unassignedGroups: slot.unassignedGroups,
                    groupCodes: slot.groups.map(
                        (group) => group.groupCode || group.deliveryGroupId
                    ),
                })),
            })

            setSlotSummaries(nextSlots)
            keepSelectedGroupInSync(nextSlots.flatMap((item) => item.groups))
        } catch (err) {
            console.error(`${DEBUG_SCOPE} loadSlotsByDate error`, err)
            setError(extractErrorMessage(err, "Không thể tải các ca giao trong ngày."))
        } finally {
            setSlotLoading(false)
        }
    }

    const handleGenerateDraftGroupsNow = async () => {
        const targetDate = selectedDate || todayDateKey

        if (!targetDate) {
            setError("Vui lòng chọn ngày cần gom đơn.")
            return
        }

        try {
            setGeneratingGroups(true)
            setError("")
            setDetailNotice("")

            const payload = {
                deliveryDate: toStartOfDayUtc(targetDate),
                maxOrdersPerGroup: 20,
            }

            debugLog("handleGenerateDraftGroupsNow:payload", payload)

            const result = await adminService.generateDraftDeliveryGroups(payload)

            debugLog("handleGenerateDraftGroupsNow:success", {
                targetDate,
                generatedCount: result?.length ?? 0,
                groups: result?.map((group) => ({
                    deliveryGroupId: group.deliveryGroupId,
                    groupCode: group.groupCode,
                    status: group.status,
                    totalOrders: group.totalOrders,
                    timeSlotDisplay: group.timeSlotDisplay,
                    deliveryDate: group.deliveryDate,
                })),
            })

            if ((result?.length ?? 0) > 0) {
                toast.success(`Đã gom ${result.length} nhóm đơn mới`)
            } else {
                toast("Chưa có đơn đủ điều kiện để gom nhóm", {
                    icon: "ℹ️",
                })
            }

            await Promise.all([loadMonthData(true), loadSlotsByDate(targetDate)])
        } catch (err) {
            console.error(`${DEBUG_SCOPE} handleGenerateDraftGroupsNow error`, err)
            setError(extractErrorMessage(err, "Không thể gom đơn ngay."))
        } finally {
            setGeneratingGroups(false)
        }
    }

    const tryLoadGroupDetail = async (
        groupId: string,
        baseDetail?: DeliveryGroupDetail | null,
        staffSource?: DeliveryStaffBoardItem[]
    ) => {
        try {
            debugLog("tryLoadGroupDetail:start", { groupId })

            const result = await adminService.getDeliveryGroupDetail(groupId)

            if (!result) {
                throw new Error("API chi tiết nhóm giao trả về rỗng")
            }

            const merged = baseDetail ? mergeGroupDetail(baseDetail, result) : result

            debugLog("tryLoadGroupDetail:success", {
                groupId,
                orderCount: merged.orders?.length ?? 0,
                status: merged.status,
            })

            setSelectedGroup(merged)
            syncSelectedStaffFromDetail(merged, staffSource)
            patchGroupIntoLocalCollections(merged)
            setDetailNotice("")
        } catch (err) {
            console.error(`${DEBUG_SCOPE} tryLoadGroupDetail error`, err)
            setError(extractErrorMessage(err, "Không thể tải chi tiết nhóm giao."))
        }
    }

    const handleSelectGroup = async (group: DeliveryGroupSummary) => {
        try {
            setDetailLoading(true)
            setError("")
            setDetailNotice("")

            debugLog("handleSelectGroup", {
                groupId: group.deliveryGroupId,
                groupCode: group.groupCode,
                status: group.status,
            })

            const fallbackDetail = buildFallbackGroupDetailFromSummary(
                enrichGroupSummary(group, collectionPoints),
                selectedGroup?.deliveryGroupId === group.deliveryGroupId
                    ? selectedGroup
                    : null
            )

            setSelectedGroup(fallbackDetail)
            syncSelectedStaffFromDetail(fallbackDetail)

            await tryLoadGroupDetail(group.deliveryGroupId, fallbackDetail)
        } finally {
            setDetailLoading(false)
        }
    }

    const handleAssignShipper = async () => {
        if (!selectedGroup?.deliveryGroupId || !assignForm.deliveryStaffId.trim()) {
            setError("Vui lòng chọn shipper trước khi phân công.")
            return
        }

        try {
            setAssigningGroupId(selectedGroup.deliveryGroupId)
            setError("")
            setDetailNotice("")

            const payload = {
                deliveryStaffId: assignForm.deliveryStaffId.trim(),
                reason: assignForm.reason.trim() || undefined,
            }

            debugLog("handleAssignShipper:payload", {
                deliveryGroupId: selectedGroup.deliveryGroupId,
                payload,
            })

            const result = isDraftGroup(selectedGroup)
                ? await adminService.assignDeliveryGroup(
                    selectedGroup.deliveryGroupId,
                    payload
                )
                : await adminService.updateDeliveryGroupAssignment(
                    selectedGroup.deliveryGroupId,
                    payload
                )

            debugLog("handleAssignShipper:success", {
                deliveryGroupId: result.deliveryGroupId,
                deliveryStaffId: result.deliveryStaffId,
                deliveryStaffName: result.deliveryStaffName,
                status: result.status,
            })

            toast.success("Đã phân công shipper thành công")
            setSelectedGroup(result)
            syncSelectedStaffFromDetail(result)
            patchGroupIntoLocalCollections(result)

            await tryLoadGroupDetail(result.deliveryGroupId, result)
            await Promise.all([loadMonthData(true), loadSlotsByDate(selectedDate)])
        } catch (err) {
            console.error(`${DEBUG_SCOPE} handleAssignShipper error`, err)
            setError(extractErrorMessage(err, "Không thể phân công shipper."))
        } finally {
            setAssigningGroupId("")
        }
    }

    const handleConfirmDraftGroup = async (groupId: string) => {
        try {
            setConfirmingGroupId(groupId)
            setError("")
            setDetailNotice("")

            debugLog("handleConfirmDraftGroup:start", { groupId })

            const result = await adminService.confirmDraftDeliveryGroup(groupId)

            debugLog("handleConfirmDraftGroup:success", {
                groupId: result.deliveryGroupId,
                status: result.status,
            })

            toast.success("Đã xác nhận nhóm draft")
            setSelectedGroup(result)
            syncSelectedStaffFromDetail(result)
            patchGroupIntoLocalCollections(result)

            await tryLoadGroupDetail(result.deliveryGroupId, result)
            await Promise.all([loadMonthData(true), loadSlotsByDate(selectedDate)])
        } catch (err) {
            console.error(`${DEBUG_SCOPE} handleConfirmDraftGroup error`, err)
            setError(extractErrorMessage(err, "Không thể xác nhận nhóm draft."))
        } finally {
            setConfirmingGroupId("")
        }
    }

    const clearSelectedStaff = () => {
        setSelectedAssignStaff(null)
        setAssignForm((prev) => ({
            ...prev,
            deliveryStaffId: "",
        }))
    }

    const resetDeliverySelectionState = () => {
        setSelectedGroup(null)
        setSlotSummaries([])
        setDetailNotice("")
        setError("")
        setSearchInput("")
        setKeyword("")
        setSelectedAssignStaff(null)
        setAssignForm({
            deliveryStaffId: "",
            reason: "",
        })
    }

    const handlePrevMonth = () => {
        resetDeliverySelectionState()

        setCurrentMonth((prev) => {
            if (prev === 1) {
                setCurrentYear((y) => y - 1)
                return 12
            }
            return prev - 1
        })
    }

    const handleNextMonth = () => {
        resetDeliverySelectionState()

        setCurrentMonth((prev) => {
            if (prev === 12) {
                setCurrentYear((y) => y + 1)
                return 1
            }
            return prev + 1
        })
    }

    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setKeyword(searchInput.trim())
    }

    const handleSelectDate = (date: string) => {
        setSelectedDate(date)
        setSelectedGroup(null)
        setDetailNotice("")
        setError("")
        setSearchInput("")
        setKeyword("")
    }

    const handleRefreshData = async () => {
        autoGeneratedDateRef.current.clear()
        await loadMonthData(true)
        if (selectedDate) {
            await loadSlotsByDate(selectedDate)
        }
    }

    useEffect(() => {
        void loadMonthData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMonth, currentYear])

    useEffect(() => {
        void loadSlotsByDate(selectedDate)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, collectionPoints.length])

    const selectedGroupId = selectedGroup?.deliveryGroupId || ""
    const timelineLoading = slotLoading || autoGeneratingDrafts

    return (
        <div className="space-y-5">
            <div className="overflow-hidden rounded-[28px] border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-indigo-50 shadow-sm">
                <div className="flex flex-col gap-4 px-6 py-6 lg:flex-row lg:items-end lg:justify-between lg:px-7">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center rounded-full border border-sky-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">
                            Admin / Delivery Planner
                        </div>

                        <h1 className="mt-3 text-2xl font-bold text-slate-900 lg:text-3xl">
                            Lịch điều phối shipper
                        </h1>

                        <p className="mt-2 text-sm text-slate-600 lg:text-[15px]">
                            Theo dõi lịch tháng, tự gom đơn đã sẵn sàng giao thành nhóm draft và phân công shipper theo từng ca.
                        </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <button
                            type="button"
                            onClick={() => void handleGenerateDraftGroupsNow()}
                            disabled={generatingGroups || !selectedDate}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {generatingGroups ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            Gom đơn ngay
                        </button>

                        <button
                            type="button"
                            onClick={() => void handleRefreshData()}
                            disabled={refreshing}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-white px-4 py-2.5 text-sm font-semibold text-sky-700 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <RefreshCcw
                                className={cn("h-4 w-4", refreshing && "animate-spin")}
                            />
                            Làm mới dữ liệu
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    title="Ngày có việc"
                    value={String(monthStats.workingDays)}
                    hint="Số ngày trong tháng có nhóm cần xử lý"
                    icon={<CalendarDays className="h-5 w-5" />}
                />
                <StatCard
                    title="Tổng nhóm giao"
                    value={String(monthStats.totalGroups)}
                    hint="Tổng số nhóm đang xuất hiện trong lịch tháng"
                    icon={<Truck className="h-5 w-5" />}
                />
                <StatCard
                    title="Tổng đơn cần điều phối"
                    value={String(monthStats.totalOrders)}
                    hint="Khối lượng đơn đang nằm trong các nhóm giao"
                    icon={<ClipboardList className="h-5 w-5" />}
                />
                <StatCard
                    title="Nhóm chưa có shipper"
                    value={String(monthStats.totalUnassigned)}
                    hint="Các nhóm cần ưu tiên xử lý trước"
                    icon={<Users2 className="h-5 w-5" />}
                />
            </div>

            {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {error}
                </div>
            ) : null}

            {autoGeneratingDrafts ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Đang tự gom đơn đủ điều kiện thành nhóm giao draft...
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Đang tải lịch điều phối...
                    </div>
                </div>
            ) : (
                <div className="space-y-5">
                    <AdminDeliveryCalendarPanel
                        currentMonth={currentMonth}
                        currentYear={currentYear}
                        selectedDate={selectedDate}
                        monthSummary={monthSummary}
                        onPrevMonth={handlePrevMonth}
                        onNextMonth={handleNextMonth}
                        onSelectDate={handleSelectDate}
                    />

                    <AdminDeliveryTimelinePanel
                        selectedDate={selectedDate}
                        slotLoading={timelineLoading}
                        searchInput={searchInput}
                        onSearchInputChange={setSearchInput}
                        onSearchSubmit={handleSearch}
                        filteredSlotSummaries={filteredSlotSummaries}
                        selectedGroupId={selectedGroupId}
                        onSelectGroup={handleSelectGroup}
                    />

                    <AdminDeliveryAssignmentPanel
                        selectedGroup={selectedGroup}
                        detailLoading={detailLoading}
                        detailNotice={detailNotice}
                        filteredStaffBoard={filteredStaffBoard}
                        staffBoard={staffBoard}
                        staffKeyword={staffKeyword}
                        onStaffKeywordChange={setStaffKeyword}
                        selectedAssignStaff={selectedAssignStaff}
                        setSelectedAssignStaff={setSelectedAssignStaff}
                        draggingStaff={draggingStaff}
                        setDraggingStaff={setDraggingStaff}
                        isAssignDropActive={isAssignDropActive}
                        setIsAssignDropActive={setIsAssignDropActive}
                        assignForm={assignForm}
                        setAssignForm={setAssignForm}
                        assigningGroupId={assigningGroupId}
                        confirmingGroupId={confirmingGroupId}
                        onAssignShipper={handleAssignShipper}
                        onConfirmDraftGroup={handleConfirmDraftGroup}
                        onClearSelectedStaff={clearSelectedStaff}
                    />
                </div>
            )}
        </div>
    )
}

export default AdminDelivery
